//@ts-ignore
import 'react-form-builder2/dist/app.css';
import React, { useContext, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addMessage } from "Redux/messageReducer";
import { RootState } from "store";
/***********************************
 ****** COMPONENT FOR ROUTING ******
 ***********************************/
// Added indexes to directories and now ./Pages export all pages in its directories
import {
  // Password, // to see if we have to have a page with reset password form
  Welcome,
  ForgotPassword,
  Dashboard,
  ProcessLibraryPage,
  NewProcessModel,
} from "./Component/Pages";
import {
  Login,
  Organizations,
  MyActivities,
  Notifications,
  NotFound,
  Processes,
  Password,
  Registration,
  // Email,
  Organization,
  Invitation,
  ExternalInvitation,
  Statistics,
  Robots,
  RobotsHelp,
  // PrivacyAndPolicy,
  Scheduled,
  FormBuilderPage,
  // Scheduled,
  SharedChatPage,
  SharedTaskPage,
  SlaEditor,
  SlaDiagram,
  AiStudioPage,
} from "pages";
import SnackbarModel from "model/snackbarModel";
import { Stack } from "@mui/material";
import Message from "./components/Message";
import { noTokenApi } from "api";
import { AuthContext, NotificationContext, OrganizationsContext } from "contexts";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Profile from "pages/Profile";
import { CONSTANTS } from "common";
import { useSSE, useVersions, useReleaseNotes } from "hooks";
import { trackPageView } from "common/utilities";
import Billing from 'components/dialogs/Billing';
import OrganizationSettings from 'pages/organizations/OrganizationSettings';
import Support from 'pages/Support';
import { Notification } from "model/notificationsModel";
import NotificationsSettings from 'components/NotificationsSettings';
import FlowentiLoader from 'components/FlowentiLoader';
import ForgotPasswordConfirm from 'Component/Pages/ForgotPassword/ForgotPasswordConfirm';
import LandingPage from 'pages/LandingPage';
import RoiPage from 'pages/roi/RoiPage';
import VersionChip from 'components/aiChat/VersionChip';
import ReleaseNotesSplash from 'components/ReleaseNotesSplash';

function PublicBillingRedirect() {
  useEffect(() => {
    const language = navigator.language.split('-')[0].toLowerCase();
    window.location.replace(`/billing/${['it', 'en', 'de', 'es', 'fr', 'pt'].includes(language) ? language : 'en'}/`);
  }, []);
  return null;
}

function App() {
  const isPrerendering = navigator.userAgent === "ReactSnap";
  const dispatch = useDispatch();
  const [translationsLoading, setTranslationsLoading] = useState(false)
  const loadingCount = useRef(0)
  const {
    actions: { resetOrganizations },
  } = useContext(OrganizationsContext);
  const {
    state: { checkInApp, user },
    actions: { setUserAndJwt },
  } = useContext(AuthContext);
  const {
    actions: { setNotificationList },
  } = useContext(NotificationContext);
  const snackbars: Array<SnackbarModel> = useSelector(
    (state: RootState) => state.snackbar
  );
  const { getVersion, updateVersion } = useVersions();
  const { releaseNote, loading: releaseNoteLoading, error: releaseNoteError, fetchNote } = useReleaseNotes();
  const [lastVersion, setLastVersion] = useState(
    process.env.REACT_APP_VERSION || ""
  );
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  useSSE<{ messages: Notification[] }>(`notifications/sse`, ({ messages }) => setNotificationList({ notifications: messages }), true);
  useEffect(() => {
    if (isPrerendering) {
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    const wsUrl = ["development", "test"].includes(process.env.REACT_APP_ENV || "") ? `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/version` : `${process.env.REACT_APP_WEBSOCKET_PROTOCOL}://${location.host}${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/version`;
    const ws = new WebSocket(wsUrl)
    ws.onopen = () => {
      ws.onmessage = ({ data: version }) => {
        if (version !== lastVersion) {
          setLastVersion(version);
        }
      };
    };
    return () => {
      ws.close();
    };
  }, [isPrerendering, lastVersion]);

  const _location = useLocation();

  useEffect(() => {
    trackPageView(_location.pathname + _location.search);
  }, [_location]);

  useEffect(() => {
    const loggedUser = JSON.parse(
      localStorage.getItem(CONSTANTS.STORE_USER) || "null"
    );
    if (loggedUser) {
      setUserAndJwt(loggedUser);
    } else {
      setUserAndJwt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (checkInApp) {
      (async () => {
        const lang = user?.i18n || navigator.language.split("-")[0];
        setTranslationsLoading(true)
        const startTime = new Date()
        try {
          const result = await noTokenApi.get(`messages/${lang}`);
          document.documentElement.setAttribute("lang", lang);
          dispatch(addMessage(result.data.messages));
          (window as any).FlowentiConsent?.ready();
        } catch (e) {
          // Translations are non-critical: a failed fetch (e.g. backend
          // unreachable in dev) must not leave the app stuck on the loader.
          console.warn("Failed to load translations", e);
        } finally {
          if (loadingCount.current === 0) {
            const endTime = new Date()
            setTimeout(() => { setTranslationsLoading(false) }, 1000 - (endTime.getTime() - startTime.getTime()))
          }
          else {
            setTranslationsLoading(false)
          }
          loadingCount.current += 1
        }
      })();
    }
  }, [dispatch, user, checkInApp]);

  useEffect(() => {
    if (!user) {
      resetOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!checkInApp || !user || translationsLoading) {
      return;
    }
    const currentVersion = process.env.REACT_APP_VERSION || "";
    const lastSeen = localStorage.getItem(CONSTANTS.RELEASE_NOTES_LAST_SEEN);
    if (lastSeen === currentVersion) {
      return;
    }
    const lang = user?.i18n || navigator.language.split("-")[0];
    (async () => {
      await fetchNote(currentVersion, lang);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInApp, user, translationsLoading]);

  useEffect(() => {
    setShowReleaseNotes(Boolean(
      releaseNote?.display &&
      user &&
      localStorage.getItem(CONSTANTS.RELEASE_NOTES_LAST_SEEN) !== (process.env.REACT_APP_VERSION || "")
    ));
  }, [releaseNote, user]);

  const handleCloseReleaseNotes = () => {
    setShowReleaseNotes(false);
    localStorage.setItem(
      CONSTANTS.RELEASE_NOTES_LAST_SEEN,
      process.env.REACT_APP_VERSION || ""
    );
  };

  if (translationsLoading) {
    return <FlowentiLoader />
  }

  return !checkInApp ? null : (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={user?.i18n || navigator.language.split("-")[0]}
    >
      <Stack
        id="message"
        spacing={2}
        sx={{
          zIndex: (_) => 9999999999999,
          position: "fixed",
          right: (_) => _.spacing(2),
          // iOS pushes the status bar / notch into the viewport when
          // viewport-fit=cover is set. Without this offset, snackbars
          // would render underneath the system bar on iPhone.
          top: (_) => `calc(${_.spacing(2)} + env(safe-area-inset-top, 0px))`,
          maxWidth: "50%",
        }}
      >
        {snackbars.map((_) => (
          <Message key={_.id} {..._} />
        ))}
      </Stack>
      <Routes>
        {/* <Route path="/privacy-policy" element={<PrivacyAndPolicy />} /> */}
        <Route path="/roi-calculator" element={<RoiPage />} />
        <Route path="/billing" element={<PublicBillingRedirect />} />
        <Route path="/invitation" element={<Invitation />} />
        <Route path="/externalinvitation" element={<ExternalInvitation />} />
        {/* <Route path="support" element={<Support />} /> mi da problemi con il routing */}
        {!user && (
          <React.Fragment>
            <Route path="/" element={<LandingPage />} />
            {/* <Route path="/landing_it" element={<LandingPage />} /> */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgotpassword_confirm" element={<ForgotPasswordConfirm />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/password" element={<Password />} />
            <Route path="/organization" element={<Organization />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/support" element={<Support />} />
          </React.Fragment>
        )}
        {/******************************
         ****** PROTECTED ROUTES ******
         ******************************/}
        {/* Public route: shared AI chat sessions. The BE exposes the
            underlying API at /helpers/:slug, but the reverse-proxy in
            front of the site serves this content under /ai/shared/:slug
            so users see a Flowenti-branded URL. The page is
            unauthenticated and works for both logged-in and anonymous
            visitors — keep it OUTSIDE the `Protected` wrapper and the
            `!user` block below. */}
        <Route path="/ai/shared" element={<SharedChatPage />} />
        <Route path="/ai/shared/:slug" element={<SharedChatPage />} />
        {/* Public route: un task di MyActivities condiviso con un link.
            Snapshot congelato servito da GET /tasks/shared/<hash> del
            backend statistiche. Anche questa sta FUORI dal wrapper
            `Protected`: chi apre il link non ha (e non deve avere) un
            account. Il path è lo stesso sul BE e qui, di proposito. */}
        <Route path="/tasks/shared" element={<SharedTaskPage />} />
        <Route path="/tasks/shared/:slug" element={<SharedTaskPage />} />
        <Route path="/dashboard/profile/support" element={<Support />} />
        <Route
          path="/dashboard"
          element={
            <Protected isLoggedIn={Boolean(user)}>
              <Dashboard newestVersion={lastVersion} getVersion={getVersion} />
            </Protected>
          }
        >
          <Route path="organizations" element={<Organizations />} />
          {/* Impostazioni dell'organizzazione: vista dedicata (prima una modale
              aperta dall'ingranaggio). Il primo segmento resta `organizations`
              come `organizations/:organizationId/billing`. */}
          <Route path="organizations/:organizationId/settings" element={<OrganizationSettings />} />
          <Route path="processes" element={<Processes />} />
          <Route path="process-history" element={<Processes history />} />
          {/* Monitor SLA rimosso come vista: i KPI SLA sono chip
              nell'intestazione di Operazioni > In tempo reale. Il Diagramma SLA
              si apre ora dai Modelli (`process/sla-diagram`). Le vecchie rotte
              restano valide (link salvati, deep-link) e reindirizzano. */}
          <Route path="processes/sla" element={<Navigate to="/dashboard/processes" replace />} />
          <Route path="processes/sla-diagram" element={<Navigate to="/dashboard/process/sla-diagram" replace />} />
          <Route path="schedules" element={<Scheduled />} />
          <Route path="myactivities" element={<MyActivities />} />
          {/* Deep-link delle notifiche SLA (decisione 2, sez. 14.2):
              stessa pagina, non una nuova. Il Server emette
              `/dashboard/myactivities/task/{idprocesstoken}`. */}
          <Route path="myactivities/task/:taskId" element={<MyActivities />} />
          {/* <Route path="process" element={<Process />} /> */}
          <Route path="process" element={<ProcessLibraryPage />} />
          <Route path="process/insert" element={<NewProcessModel />} />
          <Route
            path="profile"
            element={<Profile updateVersion={updateVersion} />}
          />
          <Route
            path="/dashboard/process/edit/:editId/"
            element={<NewProcessModel />}
          />
          {/* Editor SLA: rotte storiche, ora redirect al designer del
              modello (sezione "SLA" del pannello proprieta'). Il primo
              segmento resta `process` perche' `Dashboard.tsx:122` usa
              `routeName` per il selettore di organizzazione. */}
          <Route path="process/sla" element={<SlaEditor />} />
          {/* Diagramma SLA: aperto dal pulsante "Diagramma SLA" della barra
              dei Modelli; primo segmento `process` per il selettore di
              organizzazione. Rotta statica, precede `process/sla/:modelId`
              solo per leggibilita' (React Router la preferisce comunque). */}
          <Route path="process/sla-diagram" element={<SlaDiagram />} />
          <Route path="process/sla/:modelId" element={<SlaEditor />} />
          <Route path="statistics" element={<Statistics />} />
          {/* "AI Assistant" is the user-facing name; `ai-studio` stays as a
              redirect so bookmarks and already-shared links keep working. */}
          <Route path="ai-assistant" element={<AiStudioPage />} />
          <Route
            path="ai-studio"
            element={<Navigate to="/dashboard/ai-assistant" replace />}
          />
          <Route path="robots" element={<Robots />} />
          <Route path="robots/help" element={<RobotsHelp />} />
          <Route path="/dashboard/robots/:id/form-builder" element={<FormBuilderPage />} />
          <Route path="notifications/list" element={<Notifications />} />
          <Route path="notifications/settings" element={<NotificationsSettings />} />
          <Route path="*" element={<NotFound />} />
          <Route path={`organizations/:organizationId/billing`} element={<Billing />} />
        </Route>
        <Route
          path="*"
          element={<Navigate to="/dashboard/organizations" replace />}
        />
      </Routes>
      <VersionChip />
      <ReleaseNotesSplash
        open={Boolean(user) && showReleaseNotes}
        releaseNote={releaseNote}
        loading={releaseNoteLoading}
        error={releaseNoteError}
        onClose={handleCloseReleaseNotes}
      />
    </LocalizationProvider>
  );
}

interface ProtectedProps {
  isLoggedIn: boolean;
}
const Protected: React.FC<React.PropsWithChildren<ProtectedProps>> = ({
  isLoggedIn,
  children,
}) => {
  if (!isLoggedIn) {
    return <Navigate to="/" replace />;
  }
  return <React.Fragment>{children}</React.Fragment>;
};

export default App;
